import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ShieldCheck, FileText, Calendar, ExternalLink, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { Negociacao } from '../../../../types';
import { supabase } from '../../../../src/lib/supabase';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logNegotiationAction } from '../../../../src/lib/negotiationLogger';
import { logStageEvent, logUsageEvent } from '../../../../src/lib/logger';
import { propsFundoModal } from '../../../../src/lib/backdropModal';

interface TermItem {
    url_pdf: string;
    data_criacao: string;
    tipo_cliente?: string;
    numero_termo?: string;
    validado?: boolean;
    data_assinatura?: string;
}

interface DefinirTermoValidoModalProps {
    isOpen: boolean;
    onClose: () => void;
    negociacao: Negociacao;
    /** Chamado após salvar para recarregar o board. */
    onSuccess: () => void;
}

/** Converte um ISO em valor para <input type="datetime-local"> (hora local). */
const toLocalInput = (iso?: string): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const fmtDateTime = (iso?: string) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * Ferramenta de administrador para corrigir o caso de "duplo envio de termo":
 * quando um cliente assina o 1º termo mas um 2º termo é gerado depois e fica sem
 * assinar, a automação só acompanha o último termo e a negociação fica presa em
 * "termo_enviado" — não avança para a análise cadastral nem para o rateio.
 *
 * Aqui o admin escolhe qual termo foi efetivamente assinado, informa a data da
 * assinatura, e a negociação é movida para `termo_assinado` (entrando na fila de
 * análise cadastral). O termo escolhido é marcado com `validado: true`.
 */
const DefinirTermoValidoModal: React.FC<DefinirTermoValidoModalProps> = ({ isOpen, onClose, negociacao, onSuccess }) => {
    const { user } = useAuth();
    const { addToast } = useToast();

    // Ordena cronologicamente (1º enviado em cima) para o admin entender a sequência.
    const sortedTerms = useMemo<TermItem[]>(() => {
        const terms = (negociacao.url_termo || []) as TermItem[];
        return [...terms].sort((a, b) => new Date(a.data_criacao).getTime() - new Date(b.data_criacao).getTime());
    }, [negociacao.url_termo]);

    const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
    const [signatureDate, setSignatureDate] = useState('');
    const [saving, setSaving] = useState(false);

    // Default: termo já validado, senão o primeiro enviado (caso típico do duplo envio).
    useEffect(() => {
        if (!isOpen) return;
        const validadoIdx = sortedTerms.findIndex(t => t.validado);
        const idx = validadoIdx >= 0 ? validadoIdx : (sortedTerms.length > 0 ? 0 : null);
        setSelectedIdx(idx);
        if (idx != null) {
            const t = sortedTerms[idx];
            setSignatureDate(toLocalInput(t.data_assinatura || negociacao.data_assinatura_termo || t.data_criacao));
        }
        setSaving(false);
    }, [isOpen, sortedTerms, negociacao.data_assinatura_termo]);

    const handleSelect = (idx: number) => {
        setSelectedIdx(idx);
        const t = sortedTerms[idx];
        // Sugere a data de criação do termo escolhido como base da assinatura.
        setSignatureDate(toLocalInput(t.data_assinatura || t.data_criacao));
    };

    const handleConfirm = async () => {
        if (selectedIdx == null) {
            addToast('Selecione qual termo foi assinado pelo cliente.', 'error');
            return;
        }
        if (!signatureDate) {
            addToast('Informe a data em que o cliente assinou.', 'error');
            return;
        }
        const parsed = new Date(signatureDate);
        if (isNaN(parsed.getTime())) {
            addToast('Data de assinatura inválida.', 'error');
            return;
        }

        setSaving(true);
        try {
            const chosen = sortedTerms[selectedIdx];
            const assinaturaIso = parsed.toISOString();

            // Reconstrói url_termo marcando só o escolhido como validado.
            const novoUrlTermo = (negociacao.url_termo || []).map((t) => {
                const isChosen = t.numero_termo === chosen.numero_termo && t.data_criacao === chosen.data_criacao;
                if (isChosen) {
                    return { ...t, validado: true, data_assinatura: assinaturaIso };
                }
                const { validado, data_assinatura, ...rest } = t as TermItem;
                return rest;
            });

            // Move para o Funil de Vendas / "Termo de adesão" (estado canônico de termo
            // assinado aguardando análise), espelhando o "reenviar para análise".
            const { data: funil } = await supabase
                .from('pipelines')
                .select('id')
                .eq('name', 'Funil de Vendas')
                .single();

            const payload: any = {
                status: 'termo_assinado',
                data_assinatura_termo: assinaturaIso,
                url_termo: novoUrlTermo,
                updated_at: new Date().toISOString(),
            };
            if (funil?.id) {
                payload.pipeline_id = funil.id;
                payload.stage = 'Termo de adesão';
            }

            const { error } = await supabase
                .from('negociacoes')
                .update(payload)
                .eq('id', negociacao.id);

            if (error) throw error;

            await logNegotiationAction(
                negociacao.id,
                user?.id,
                'Admin definiu o termo assinado válido (correção de duplo envio)',
                {
                    numero_termo: chosen.numero_termo || null,
                    data_assinatura: assinaturaIso,
                    status_anterior: negociacao.status,
                }
            );
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'definir_termo_valido', origem: 'acao' });

            await logStageEvent({
                negociacaoId: negociacao.id,
                userId: user?.id,
                fromStage: negociacao.stage,
                toStage: 'Termo de adesão',
                fromStatus: negociacao.status,
                toStatus: 'termo_assinado',
            });

            addToast('Termo definido. Negociação enviada para a validação de dados.', 'success');
            onSuccess();
            onClose();
        } catch (e: any) {
            console.error('Erro ao definir termo válido:', e);
            addToast('Erro ao definir termo: ' + (e.message || 'erro desconhecido'), 'error');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 dark:border-slate-700/50 bg-indigo-50/60 dark:bg-indigo-950/30">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg border border-indigo-200 dark:border-indigo-800/50">
                            <ShieldCheck size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100 font-poppins">Definir termo assinado (Admin)</h2>
                            <p className="text-sm text-gray-500 dark:text-slate-400">{negociacao.cliente_nome}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
                    {/* Aviso explicativo */}
                    <div className="flex gap-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
                        <AlertTriangle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
                            Use quando houve <strong>duplo envio de termo</strong>: o cliente assinou um termo mas a automação acompanhou só o último (não assinado), deixando a negociação presa. Escolha o termo que foi <strong>realmente assinado</strong> — a negociação irá para <strong>Termo assinado</strong> e entrará na fila de <strong>validação de dados</strong>.
                        </p>
                    </div>

                    {sortedTerms.length === 0 ? (
                        <div className="text-center py-8 text-sm text-gray-500 dark:text-slate-400 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-xl">
                            Esta negociação não possui termos gerados.
                        </div>
                    ) : (
                        <div>
                            <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
                                Qual termo o cliente assinou?
                            </label>
                            <div className="space-y-2">
                                {sortedTerms.map((term, index) => {
                                    const selected = selectedIdx === index;
                                    return (
                                        <button
                                            key={`${term.numero_termo}-${term.data_criacao}`}
                                            type="button"
                                            onClick={() => handleSelect(index)}
                                            className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-all ${selected
                                                ? 'border-indigo-400 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-300 dark:ring-indigo-600'
                                                : 'border-gray-200 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-700 bg-white dark:bg-slate-800/40'
                                                }`}
                                        >
                                            <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${selected ? 'border-indigo-500' : 'border-gray-300 dark:border-slate-600'}`}>
                                                {selected && <span className="w-2 h-2 rounded-full bg-indigo-500" />}
                                            </div>
                                            <FileText size={16} className="text-gray-400 dark:text-slate-500 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-mono font-semibold text-gray-700 dark:text-slate-200">
                                                        {term.numero_termo || 'N/A'}
                                                    </span>
                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400">
                                                        {index + 1}º enviado
                                                    </span>
                                                    {term.validado && (
                                                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                                            <CheckCircle2 size={11} /> Validado
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-slate-500 mt-0.5">
                                                    <Calendar size={12} />
                                                    Gerado em {fmtDateTime(term.data_criacao)}
                                                </div>
                                            </div>
                                            <a
                                                href={term.url_pdf}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-800 hover:text-indigo-600 transition-colors"
                                                title="Abrir termo"
                                            >
                                                Ver <ExternalLink size={12} />
                                            </a>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Data de assinatura */}
                            <div className="mt-5">
                                <label className="block text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400 mb-2">
                                    Data e hora da assinatura
                                </label>
                                <input
                                    type="datetime-local"
                                    value={signatureDate}
                                    onChange={(e) => setSignatureDate(e.target.value)}
                                    className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-gray-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 dark:focus:ring-indigo-600"
                                />
                                <p className="text-[11px] text-gray-400 dark:text-slate-500 mt-1.5">
                                    Confira no Autentique a data real em que o cliente assinou. Ela alimenta o SLA da análise e os relatórios de "Termo Assinado".
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 dark:border-slate-700/50 bg-gray-50 dark:bg-slate-800/80 flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        disabled={saving}
                        className="px-4 py-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-200 rounded-lg text-sm font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-60"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={saving || sortedTerms.length === 0 || selectedIdx == null}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                        Confirmar e enviar para análise
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DefinirTermoValidoModal;
